import React from "react";

import * as auth from "@client/features/auth";
import {notify} from "@client/features/notifications";

import * as graphics from "@client/app/graphics";
import * as typography from "@client/shared/typography";

import {Register} from "@client/site/components";
import * as forms from "@client/site/forms";
import {useAppDispatch} from "@client/store";
import {server} from "@reactivated";

export type RegistrationJourneyProps = {
    /*
    onClose?: (handler: {
        setIsSaving: (status: boolean) => void;
        registrationForm: ReturnType<(typeof auth)["useRegistrationForm"]>;
    }) => void;
     */
    close: (registered: server.core.rpc.Profile.output | null) => void;
};

export function RegistrationJourney(props: RegistrationJourneyProps) {
    const [isSaving, setIsSaving] = React.useState(false);
    const [isLogin, setIsLogin] = React.useState(false);
    const dispatch = useAppDispatch();

    const registrationForm = auth.useRegistrationForm();
    const loginForm = auth.useLoginForm();

    const register = async () => {
        setIsSaving(true);

        const registrationResult = await server.core.rpc.register({
            email: registrationForm.values.email,
            with_notification: false,
            invite_code: "i am not a robot",
            recipe_uuid: null,
            source: null,
            platform: "web",
        });

        if (registrationResult.type == "invalid") {
            registrationForm.processErrors(registrationResult.errors);
            setIsSaving(false);
            return;
        } else if (registrationResult.type != "success") {
            setIsSaving(false);
            return;
        }

        props.close(registrationResult.data);
    };

    const login = async () => {
        setIsSaving(true);

        const loginResult = await server.core.rpc.login({
            email: loginForm.values.email,
            password: loginForm.values.password,
        });

        if (loginResult.type == "invalid") {
            if (
                loginResult.errors.length == 1 &&
                typeof loginResult.errors[0] == "string"
            ) {
                dispatch(notify(loginResult.errors[0], {type: "error"}));
                loginForm.processErrors([]);
            } else {
                loginForm.processErrors(loginResult.errors);
            }
            setIsSaving(false);
            return;
        } else if (loginResult.type != "success") {
            setIsSaving(false);
            return;
        }

        props.close(loginResult.data);
    };

    if (isLogin) {
        return (
            <form
                autoComplete="off"
                noValidate
                onSubmit={(event) => {
                    event.preventDefault();
                    void login();
                }}
                className="flex flex-col gap-4 p-6"
            >
                <h2 className="flex items-center gap-2 text-lg whitespace-nowrap">
                    <div className="size-4">
                        <graphics.Icon className="size-full" />
                    </div>
                    Login to Joy Health Tracker
                </h2>
                <div className="flex flex-col gap-4">
                    <forms.PField field={loginForm.fields.email} />
                    <forms.PField field={loginForm.fields.password} />
                    <typography.Button
                        submit
                        disabled={
                            loginForm.values.email == "" ||
                            loginForm.values.password == "" ||
                            isSaving
                        }
                    >
                        Login
                    </typography.Button>
                    <p className="text-sm/2">
                        Don’t have an account?{" "}
                        <button
                            className="font-semibold text-indigo-600"
                            onClick={() => setIsLogin(false)}
                        >
                            Create one
                        </button>
                        .
                    </p>
                    <p className="text-sm/2">
                        Forgot your password?{" "}
                        <a className="font-semibold text-indigo-600" href="/reset/">
                            Reset it
                        </a>
                    </p>
                </div>
            </form>
        );
    }

    return (
        <form
            autoComplete="off"
            noValidate
            onSubmit={(event) => {
                event.preventDefault();
                void register();
            }}
            className="flex flex-col gap-4 p-6"
        >
            <h2 className="flex items-center gap-2 text-lg whitespace-nowrap">
                <div className="size-4">
                    <graphics.Icon className="size-full" />
                </div>
                Sign up for Joy Health Tracker
            </h2>
            <p className="text-sm/6">
                Track your nutrition, workouts and habits.
                <br />
                All in <strong className="text-indigo-600">one</strong> place.
            </p>
            <forms.PField field={registrationForm.fields.email} />
            <Register isSubmitting={registrationForm.values.email == "" || isSaving} />

            <typography.P small muted>
                Joy is free for 7 days. Afterwards, it’s just 5 bucks a month.
            </typography.P>
            <typography.P small muted>
                Have an account?{" "}
                <button
                    className="font-semibold text-indigo-600"
                    onClick={() => setIsLogin(true)}
                >
                    Login
                </button>
            </typography.P>
        </form>
    );
}
